import FilePresentOutlined from '@mui/icons-material/FilePresentOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FileOutput } from 'dtos/diligence/FileOutput';
import { observer } from 'mobx-react-lite';
import { DateTime } from 'luxon';
import { noThumbnailAvailable } from 'dtos/diligence/common';
import Delete from '@mui/icons-material/Delete';
import { ExpandMore } from '@mui/icons-material';
import { DiligenceFilesStore } from 'stores/diligence/DiligenceFilesStore';

function renderSize(size: number): string {
  if (size > 1048576) {
    return +(size / 1048576).toFixed(1) + ' MB';
  }
  return +(size / 1024).toFixed(1) + ' KB';
}

function renderDate(date: string): string {
  return DateTime.fromISO(date).toLocal().toFormat('MM-dd-yyyy');
}

function renderUrl(url: string): string {
  return url;
}

const ListItemButtonStyled = styled(ListItemButton)(({ theme }) => ({
  padding: '8px 14px 8px 8px',
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
  },
  backgroundColor: 'white',
  border: '1px solid rgba(34, 34, 34, 0.12)',
  borderRadius: '4px',
  '&:hover:has(button:hover)': {
    backgroundColor: 'white',
  },
}));

type FilesListProps = {
  sx?: any;
  files: FileOutput[];
  onDeleteClick: (event: React.MouseEvent, file: FileOutput) => void;
  filesStore: DiligenceFilesStore;
  setCategorySubmitted: (category: string) => void;
  setCategoryIncomplete: (category: string) => void;
  categories: string[];
};

const FilesList = observer(
  ({
    sx,
    files,
    onDeleteClick,
    filesStore,
    setCategorySubmitted,
    setCategoryIncomplete,
    categories,
  }: FilesListProps) => {
    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const handleItemClick = (event: React.MouseEvent, url: string) => {
      setTimeout(() => {
        window.open(url, '_blank');
      });
      event.stopPropagation();
      event.preventDefault();
    };

    return (
      <Grid container spacing={{ xs: 1, sm: 2 }} data-testid="files-list" sx={sx}>
        <Grid item xs={12} sx={{ minWidth: 0 }} container spacing={{ xs: 1, sm: 2 }} alignItems="center">
          <Grid item xs={6.5}>
            <strong>File</strong>
          </Grid>
          <Grid item xs={3}>
            <strong>Category</strong>
          </Grid>
          <Grid item xs={2.5} justifySelf="flex-end">
            <strong>Actions</strong>
          </Grid>
        </Grid>
        {files.map((file) => (
          <Grid
            item
            xs={12}
            key={file.id}
            sx={{ minWidth: 0 }}
            container
            spacing={{ xs: 1, sm: 2 }}
            alignItems="center"
          >
            <Grid item xs={6.5}>
              <ListItemButtonStyled onClick={(event) => handleItemClick(event, renderUrl(file.url))}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'initial', marginRight: '16px' }}>
                    {file.thumbnail && file.thumbnail !== noThumbnailAvailable ? (
                      <div style={{ minWidth: 32 }}>
                        <img src={'data:image/jpeg;base64, ' + file.thumbnail} alt="thumbnail" />
                      </div>
                    ) : (
                      <FilePresentOutlined fontSize="large" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {file.name}
                        <OpenInNew sx={{ fontSize: '16px', marginLeft: '6px' }} />
                      </div>
                    }
                    secondary={renderSize(file.size) + ' - ' + renderDate(file.lastModified)}
                    sx={{
                      margin: 0,
                      whiteSpace: 'nowrap',
                      '& > span': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      '& > p': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                </div>
              </ListItemButtonStyled>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <Select
                  id={`category-select-${file.id}`}
                  size="medium"
                  IconComponent={ExpandMore}
                  value={file.category ?? ''}
                  displayEmpty
                  onChange={(event) => {
                    const oldCategory = file.category ?? '';
                    const category = event.target.value;
                    filesStore.updateCategory(file, category);
                    const otherFileInCategory = filesStore.files.find((f) => (f.category ?? '') === oldCategory);
                    if (oldCategory && !otherFileInCategory) setCategoryIncomplete(oldCategory);
                    if (category) {
                      setCategorySubmitted(category);
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    Select category
                  </MenuItem>
                  {categories.map((value) => {
                    return (
                      <MenuItem key={value} value={value} sx={{ display: 'flex', justifyContent: 'center' }}>
                        {value}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2.5} container spacing={1} direction="column" justifyContent="space-between">
              <Grid item>
                <Tooltip title="Delete">
                  {xsScreen ? (
                    <IconButton
                      aria-label="delete"
                      data-testid="file-delete"
                      onClick={(event) => onDeleteClick(event, file)}
                    >
                      <Delete />
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      aria-label="delete"
                      data-testid="file-delete"
                      onClick={(event) => onDeleteClick(event, file)}
                      size="small"
                      sx={{ backgroundColor: 'white' }}
                    >
                      Delete
                    </Button>
                  )}
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }
);

export { FilesList };
